export type CprBoundaryControlPoint = { row: number; x: number };

export type CprBoundaryModel = {
  rowCount: number;
  colCount: number;
  rowStart: number;
  rowEnd: number;
  leftX: number[];
  rightX: number[];
  leftControlPoints: CprBoundaryControlPoint[];
  rightControlPoints: CprBoundaryControlPoint[];
};

export type CprBoundarySnapshot = {
  model: CprBoundaryModel;
  mask: Uint8Array;
};

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));

const cloneModel = (model: CprBoundaryModel): CprBoundaryModel => ({
  rowCount: model.rowCount,
  colCount: model.colCount,
  rowStart: model.rowStart,
  rowEnd: model.rowEnd,
  leftX: model.leftX.slice(),
  rightX: model.rightX.slice(),
  leftControlPoints: model.leftControlPoints.map((p) => ({ ...p })),
  rightControlPoints: model.rightControlPoints.map((p) => ({ ...p })),
});

export function cloneBoundarySnapshot(
  snapshot: CprBoundarySnapshot,
): CprBoundarySnapshot {
  return {
    model: cloneModel(snapshot.model),
    mask: new Uint8Array(snapshot.mask),
  };
}

function rowIntersections(contour: [number, number][], y: number): number[] {
  const xs: number[] = [];
  for (let i = 0; i < contour.length - 1; i += 1) {
    const [x1, y1] = contour[i];
    const [x2, y2] = contour[i + 1];
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);
    if (y < minY || y >= maxY || y1 === y2) continue;
    const t = (y - y1) / (y2 - y1);
    xs.push(x1 + t * (x2 - x1));
  }
  xs.sort((a, b) => a - b);
  return xs;
}

function fillMissingEdgesInRange(
  arr: number[],
  start: number,
  end: number,
  fallback: number,
) {
  let last = Number.NaN;
  for (let i = start; i <= end; i += 1) {
    if (Number.isFinite(arr[i])) last = arr[i];
    else if (Number.isFinite(last)) arr[i] = last;
  }
  last = Number.NaN;
  for (let i = end; i >= start; i -= 1) {
    if (Number.isFinite(arr[i])) last = arr[i];
    else if (Number.isFinite(last)) arr[i] = last;
  }
  for (let i = start; i <= end; i += 1) {
    if (!Number.isFinite(arr[i])) arr[i] = fallback;
  }
}

function smoothMedianInRange(
  arr: number[],
  start: number,
  end: number,
  radius: number,
) {
  if (radius <= 0 || end - start < 2) return arr.slice();
  const out = new Array<number>(arr.length);
  for (let i = 0; i < arr.length; i += 1) out[i] = arr[i];
  for (let i = start; i <= end; i += 1) {
    const w: number[] = [];
    for (let k = i - radius; k <= i + radius; k += 1) {
      if (k < start || k > end) continue;
      w.push(arr[k]);
    }
    w.sort((a, b) => a - b);
    out[i] = w[Math.floor(w.length / 2)] ?? arr[i];
  }
  return out;
}

function pickCenterRunFromRow(
  mask: Uint8Array,
  width: number,
  row: number,
  centerX: number,
) {
  const offset = row * width;
  let x = 0;
  let bestContainingL = -1;
  let bestContainingR = -1;
  let bestContainingLen = -1;
  let bestL = -1;
  let bestR = -1;
  let bestScore = Number.POSITIVE_INFINITY;

  while (x < width) {
    while (x < width && mask[offset + x] === 0) x += 1;
    if (x >= width) break;
    const l = x;
    while (x < width && mask[offset + x] > 0) x += 1;
    const r = x - 1;
    const cx = (l + r) * 0.5;
    const runLen = r - l + 1;
    const containsCenter = l <= centerX && centerX <= r;
    if (containsCenter && runLen > bestContainingLen) {
      bestContainingLen = runLen;
      bestContainingL = l;
      bestContainingR = r;
    }
    // Prefer runs near centerline, but bias toward longer runs when similar distance.
    const score = Math.abs(cx - centerX) - runLen * 0.15;
    if (score < bestScore) {
      bestScore = score;
      bestL = l;
      bestR = r;
    }
  }

  if (bestContainingL >= 0 && bestContainingR >= bestContainingL) {
    return { left: bestContainingL, right: bestContainingR };
  }
  if (bestL < 0 || bestR < bestL) return null;
  return { left: bestL, right: bestR };
}

function smoothMedian(arr: number[], radius: number) {
  return smoothMedianInRange(arr, 0, arr.length - 1, radius);
}

function smoothWindowInRange(
  arr: number[],
  start: number,
  end: number,
  radius: number,
) {
  if (radius <= 0 || end - start < 2) return arr.slice();
  const out = arr.slice();
  for (let row = start; row <= end; row += 1) {
    let wsum = 0;
    let vsum = 0;
    for (let k = row - radius; k <= row + radius; k += 1) {
      if (k < start || k > end) continue;
      const dist = Math.abs(k - row);
      const w = radius + 1 - dist;
      wsum += w;
      vsum += arr[k] * w;
    }
    out[row] = wsum > 0 ? vsum / wsum : arr[row];
  }
  return out;
}

export function buildBoundaryModelFromLabelMask(
  mask: Uint8Array,
  width: number,
  height: number,
  centerX: number,
): CprBoundaryModel | null {
  const LEFT_EDGE_BIAS = 0.0;
  const RIGHT_EDGE_BIAS = 0.8;

  const leftX = new Array<number>(height).fill(Number.NaN);
  const rightX = new Array<number>(height).fill(Number.NaN);
  let rowStart = height;
  let rowEnd = -1;

  for (let row = 0; row < height; row += 1) {
    const run = pickCenterRunFromRow(mask, width, row, centerX);
    if (!run) continue;
    leftX[row] = run.left + LEFT_EDGE_BIAS;
    rightX[row] = run.right + RIGHT_EDGE_BIAS;
    rowStart = Math.min(rowStart, row);
    rowEnd = Math.max(rowEnd, row);
  }
  if (rowEnd < rowStart) return null;

  fillMissingEdgesInRange(leftX, rowStart, rowEnd, centerX - 1);
  fillMissingEdgesInRange(rightX, rowStart, rowEnd, centerX + 1);

  const lSm = smoothMedianInRange(leftX, rowStart, rowEnd, 1);
  const rSm = smoothMedianInRange(rightX, rowStart, rowEnd, 1);

  const controlStep = clamp(Math.floor((rowEnd - rowStart + 1) / 48), 2, 8);
  const leftControlPoints: CprBoundaryControlPoint[] = [];
  const rightControlPoints: CprBoundaryControlPoint[] = [];
  for (let row = rowStart; row <= rowEnd; row += controlStep) {
    leftControlPoints.push({ row, x: lSm[row] });
    rightControlPoints.push({ row, x: rSm[row] });
  }
  if (
    !leftControlPoints.length ||
    leftControlPoints[leftControlPoints.length - 1].row !== rowEnd
  ) {
    leftControlPoints.push({ row: rowEnd, x: lSm[rowEnd] });
    rightControlPoints.push({ row: rowEnd, x: rSm[rowEnd] });
  }

  return {
    rowCount: height,
    colCount: width,
    rowStart,
    rowEnd,
    leftX: lSm,
    rightX: rSm,
    leftControlPoints,
    rightControlPoints,
  };
}

function interpolateFromControls(
  points: CprBoundaryControlPoint[],
  rowCount: number,
) {
  const dense = new Array<number>(rowCount).fill(0);
  if (!points.length) return dense;

  const sorted = points.slice().sort((a, b) => a.row - b.row);
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const p0 = sorted[i];
    const p1 = sorted[i + 1];
    const dr = Math.max(1, p1.row - p0.row);
    for (let row = p0.row; row <= p1.row; row += 1) {
      const t = (row - p0.row) / dr;
      dense[row] = p0.x * (1 - t) + p1.x * t;
    }
  }

  for (let row = 0; row < sorted[0].row; row += 1) {
    dense[row] = sorted[0].x;
  }
  for (let row = sorted[sorted.length - 1].row; row < rowCount; row += 1) {
    dense[row] = sorted[sorted.length - 1].x;
  }

  return smoothMedian(dense, 1);
}

export function rebuildDenseBoundaryFromControls(model: CprBoundaryModel) {
  const left = interpolateFromControls(model.leftControlPoints, model.rowCount);
  const right = interpolateFromControls(
    model.rightControlPoints,
    model.rowCount,
  );

  for (let i = 0; i < model.rowStart; i += 1) {
    model.leftX[i] = Number.NaN;
    model.rightX[i] = Number.NaN;
  }
  for (let i = model.rowEnd + 1; i < model.rowCount; i += 1) {
    model.leftX[i] = Number.NaN;
    model.rightX[i] = Number.NaN;
  }
  for (let i = model.rowStart; i <= model.rowEnd; i += 1) {
    model.leftX[i] = clamp(left[i], 0, model.colCount - 1);
    model.rightX[i] = clamp(
      Math.max(model.leftX[i] + 0.75, right[i]),
      0,
      model.colCount - 1,
    );
  }
}

export function buildBoundaryPolygon(model: CprBoundaryModel) {
  const left: [number, number][] = [];
  const right: [number, number][] = [];
  for (let row = model.rowStart; row <= model.rowEnd; row += 1) {
    if (
      !Number.isFinite(model.leftX[row]) ||
      !Number.isFinite(model.rightX[row])
    ) {
      continue;
    }
    const y = row + 0.5;
    left.push([model.leftX[row], y]);
    right.push([model.rightX[row], y]);
  }
  if (left.length < 2 || right.length < 2) return [];
  return [...left, ...right.reverse(), left[0]];
}

export function rasterizeBoundaryModel(model: CprBoundaryModel) {
  const polygon = buildBoundaryPolygon(model);
  const mask = new Uint8Array(model.rowCount * model.colCount);
  if (polygon.length < 4) return mask;

  for (let row = model.rowStart; row <= model.rowEnd; row += 1) {
    const y = row + 0.5;
    const xs = rowIntersections(polygon, y);
    if (xs.length < 2) continue;
    const lx = clamp(Math.floor(xs[0]), 0, model.colCount - 1);
    const rx = clamp(Math.ceil(xs[xs.length - 1]), 0, model.colCount - 1);
    const offset = row * model.colCount;
    for (let x = lx; x <= rx; x += 1) {
      mask[offset + x] = 255;
    }
  }

  return mask;
}

export function modelToLocalOutline(
  model: CprBoundaryModel,
  spacingX: number,
  spacingY: number,
) {
  const left: [number, number][] = [];
  const right: [number, number][] = [];
  for (let row = model.rowStart; row <= model.rowEnd; row += 1) {
    if (
      !Number.isFinite(model.leftX[row]) ||
      !Number.isFinite(model.rightX[row])
    ) {
      continue;
    }
    const y = (row + 0.5) * spacingY;
    left.push([model.leftX[row] * spacingX, y]);
    right.push([model.rightX[row] * spacingX, y]);
  }
  return [left, right];
}

export function controlPointsToLocal(
  controls: CprBoundaryControlPoint[],
  spacingX: number,
  spacingY: number,
) {
  return controls.map((p) => ({
    x: p.x * spacingX,
    y: (p.row + 0.5) * spacingY,
  }));
}

export function cloneBoundaryModel(model: CprBoundaryModel) {
  return cloneModel(model);
}

export type LocalDeformSide = 'left' | 'right';

export type LocalDeformOptions = {
  radiusRows: number;
  sigmaRows: number;
  minThickness: number;
  smoothRadiusRows?: number;
};

export function applyLocalBoundaryDeformation(
  model: CprBoundaryModel,
  baseLeftX: number[],
  baseRightX: number[],
  anchorRow: number,
  deltaX: number,
  side: LocalDeformSide,
  options: LocalDeformOptions,
) {
  const left = baseLeftX.slice();
  const right = baseRightX.slice();

  const radius = Math.max(1, Math.floor(options.radiusRows));
  const sigma = Math.max(0.5, options.sigmaRows);
  const minThickness = Math.max(0.5, options.minThickness);
  const smoothRadius = Math.max(0, Math.floor(options.smoothRadiusRows ?? 2));

  const rowLo = Math.max(model.rowStart, anchorRow - radius);
  const rowHi = Math.min(model.rowEnd, anchorRow + radius);

  const gauss = (dist: number) =>
    Math.exp(-(dist * dist) / (2 * sigma * sigma));

  for (let row = rowLo; row <= rowHi; row += 1) {
    if (!Number.isFinite(left[row]) || !Number.isFinite(right[row])) continue;
    const influence = gauss(Math.abs(row - anchorRow));

    if (side === 'left') {
      left[row] = left[row] + deltaX * influence;
      left[row] = clamp(left[row], 0, model.colCount - 1);
      right[row] = Math.max(right[row], left[row] + minThickness);
      right[row] = clamp(right[row], 0, model.colCount - 1);
    } else {
      right[row] = right[row] + deltaX * influence;
      right[row] = clamp(right[row], 0, model.colCount - 1);
      left[row] = Math.min(left[row], right[row] - minThickness);
      left[row] = clamp(left[row], 0, model.colCount - 1);
    }
  }

  if (smoothRadius > 0) {
    if (side === 'left') {
      const smoothed = smoothWindowInRange(left, rowLo, rowHi, smoothRadius);
      for (let row = rowLo; row <= rowHi; row += 1) {
        if (!Number.isFinite(smoothed[row])) continue;
        left[row] = clamp(smoothed[row], 0, model.colCount - 1);
        right[row] = clamp(
          Math.max(right[row], left[row] + minThickness),
          0,
          model.colCount - 1,
        );
      }
    } else {
      const smoothed = smoothWindowInRange(right, rowLo, rowHi, smoothRadius);
      for (let row = rowLo; row <= rowHi; row += 1) {
        if (!Number.isFinite(smoothed[row])) continue;
        right[row] = clamp(smoothed[row], 0, model.colCount - 1);
        left[row] = clamp(
          Math.min(left[row], right[row] - minThickness),
          0,
          model.colCount - 1,
        );
      }
    }
  }

  return { leftX: left, rightX: right };
}

export function syncControlPointsFromDense(
  model: CprBoundaryModel,
  side?: LocalDeformSide,
) {
  if (!side || side === 'left') {
    for (let i = 0; i < model.leftControlPoints.length; i += 1) {
      const row = model.leftControlPoints[i].row;
      if (row < 0 || row >= model.rowCount) continue;
      if (!Number.isFinite(model.leftX[row])) continue;
      model.leftControlPoints[i].x = model.leftX[row];
    }
  }
  if (!side || side === 'right') {
    for (let i = 0; i < model.rightControlPoints.length; i += 1) {
      const row = model.rightControlPoints[i].row;
      if (row < 0 || row >= model.rowCount) continue;
      if (!Number.isFinite(model.rightX[row])) continue;
      model.rightControlPoints[i].x = model.rightX[row];
    }
  }
}

export function buildAnnulusMask(
  wallMask: Uint8Array,
  lumenMask: Uint8Array,
) {
  const len = Math.min(wallMask.length, lumenMask.length);
  const out = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    out[i] = wallMask[i] > 0 && lumenMask[i] === 0 ? 255 : 0;
  }
  return out;
}
